import { MarkerType, type Edge, type Node } from '@xyflow/react';
import type { Equipment, HydraulicLink, IrrigationBlock, Sensor } from '../types/digitalTwin';
import type { EquipmentTelemetry } from './digitalTwinTelemetry';

export type EquipmentNodeData = {
  kind: 'equipment';
  equipment: Equipment;
  sensors: Sensor[];
  detailed: boolean;
  showSensors: boolean;
  telemetry?: EquipmentTelemetry;
};

export type BlockNodeData = {
  kind: 'block';
  block: IrrigationBlock;
};

export type DigitalTwinNode = Node<EquipmentNodeData | BlockNodeData>;

const FALLBACK_ORIGIN = { x: 80, y: 100 };
const COLUMN_GAP = 240;
const ROW_GAP = 170;

export function fallbackPositions(
  equipment: Equipment[],
  links: HydraulicLink[],
): Map<string, { x: number; y: number }> {
  const ids = new Set(equipment.map((item) => item.id));
  const incoming = new Map(equipment.map((item) => [item.id, 0]));
  const outgoing = new Map(equipment.map((item) => [item.id, [] as string[]]));

  links.forEach((link) => {
    if (!ids.has(link.source_equipment_id) || !ids.has(link.target_equipment_id)) return;
    incoming.set(link.target_equipment_id, (incoming.get(link.target_equipment_id) || 0) + 1);
    outgoing.get(link.source_equipment_id)?.push(link.target_equipment_id);
  });

  const roots = equipment
    .filter((item) => incoming.get(item.id) === 0)
    .map((item) => item.id)
    .sort();
  const queue = (roots.length ? roots : equipment.map((item) => item.id).sort())
    .map((id) => ({ id, depth: 0 }));
  const depths = new Map<string, number>();

  while (queue.length) {
    const current = queue.shift()!;
    if ((depths.get(current.id) ?? -1) >= current.depth) continue;
    depths.set(current.id, current.depth);
    (outgoing.get(current.id) || []).sort().forEach((id) => {
      queue.push({ id, depth: current.depth + 1 });
    });
  }

  equipment.forEach((item) => {
    if (!depths.has(item.id)) depths.set(item.id, 0);
  });

  const rows = new Map<number, string[]>();
  Array.from(depths.entries()).forEach(([id, depth]) => {
    rows.set(depth, [...(rows.get(depth) || []), id].sort());
  });

  const positions = new Map<string, { x: number; y: number }>();
  rows.forEach((row, depth) => row.forEach((id, index) => {
    positions.set(id, {
      x: FALLBACK_ORIGIN.x + depth * COLUMN_GAP,
      y: FALLBACK_ORIGIN.y + index * ROW_GAP,
    });
  }));
  return positions;
}

function edgeLabel(link: HydraulicLink): string {
  return [
    link.link_type,
    link.diameter_mm ? `Ø ${link.diameter_mm} mm` : null,
    link.length_m ? `${link.length_m} m` : null,
    link.material,
  ].filter(Boolean).join(' · ');
}

export function buildDigitalTwinGraph({
  equipment,
  blocks,
  sensors,
  links,
  showSensors,
  showBlocks,
  detailed,
  telemetryByEquipmentId = {},
}: {
  equipment: Equipment[];
  blocks: IrrigationBlock[];
  sensors: Sensor[];
  links: HydraulicLink[];
  showSensors: boolean;
  showBlocks: boolean;
  detailed: boolean;
  telemetryByEquipmentId?: Record<string, EquipmentTelemetry>;
}): { nodes: DigitalTwinNode[]; edges: Edge[] } {
  const fallback = fallbackPositions(equipment, links);
  const byEquipment = new Map<string, Sensor[]>();
  sensors.forEach((sensor) => {
    if (!sensor.equipment_id) return;
    byEquipment.set(sensor.equipment_id, [...(byEquipment.get(sensor.equipment_id) || []), sensor]);
  });

  const blockNodes: DigitalTwinNode[] = showBlocks ? blocks.map((block, index) => ({
    id: `block:${block.id}`,
    type: 'irrigationBlock',
    position: block.position || { x: 500, y: 160 + index * 220 },
    data: { kind: 'block', block },
    selectable: true,
    draggable: false,
    zIndex: 0,
    style: { width: 340, height: 180 },
  })) : [];

  const equipmentNodes: DigitalTwinNode[] = equipment.map((item) => ({
    id: item.id,
    type: 'equipment',
    position: item.position || fallback.get(item.id) || FALLBACK_ORIGIN,
    data: {
      kind: 'equipment',
      equipment: item,
      sensors: byEquipment.get(item.id) || [],
      detailed,
      showSensors,
      telemetry: telemetryByEquipmentId[item.id],
    },
    selectable: true,
    draggable: false,
    zIndex: 2,
  }));

  const validIds = new Set(equipment.map((item) => item.id));
  const edges: Edge[] = links
    .filter((link) => validIds.has(link.source_equipment_id) && validIds.has(link.target_equipment_id))
    .map((link) => ({
      id: link.id,
      source: link.source_equipment_id,
      target: link.target_equipment_id,
      type: 'smoothstep',
      label: edgeLabel(link),
      markerEnd: { type: MarkerType.ArrowClosed, color: '#1687a7' },
      style: { stroke: '#1687a7', strokeWidth: 2.5 },
      labelStyle: { fill: '#31515c', fontSize: 10, fontWeight: 600 },
      labelBgStyle: { fill: '#f4fbfc', fillOpacity: 0.94 },
      labelBgPadding: [5, 3],
      labelBgBorderRadius: 4,
    }));

  return { nodes: [...blockNodes, ...equipmentNodes], edges };
}
